import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFT_PROPERTY = '_lode_gift';
/** Hidden line property on free gifts added with a bundle option; its value is the units the gift needs. */
export const BUNDLE_GIFT_PROPERTY = '_lode_bundle_gift';

/**
 * Sends a cart mutation and broadcasts it as a CartLinesUpdateEvent so the theme's cart sections
 * (drawer, cart page, cart icon) re-render from the returned section HTML.
 *
 * @param {HTMLElement} source - Element that dispatches the event.
 * @param {'add' | 'change' | 'update'} kind - Which cart endpoint to call.
 * @param {Record<string, unknown>} payload - Request body without the sections fields.
 * @param {{ action: 'add' | 'update' | 'remove', lines: Array<Record<string, unknown>> }} event - Event description.
 * @param {string} [label] - `detail.source` reported to listeners.
 */
export async function mutateCart(source, kind, payload, event, label = 'cart-extras') {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({ action: event.action, context: 'cart', lines: event.lines, promise: deferred.promise })
  );

  try {
    const url =
      kind === 'add'
        ? Theme.routes.cart_add_url
        : `${kind === 'update' ? Theme.routes.cart_update_url : Theme.routes.cart_change_url}.js`;
    const body = JSON.stringify({ ...payload, sections: sectionIds.join(','), sections_url: window.location.pathname });
    const response = await fetch(url, fetchConfig('json', { body }));
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart update failed');

    // /cart/add returns the added items only; read the full cart for listeners that need it.
    const cart = kind === 'add' ? await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json()) : data;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections ?? undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: label,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * "Subscribe & save" offer for a one-time cart line: switches the line to a selling plan in place.
 *
 * @typedef {object} UpgradeRefs
 * @property {HTMLButtonElement} button
 *
 * @extends Component<UpgradeRefs>
 */
class CartUpgradeComponent extends Component {
  async upgrade() {
    const { button } = this.refs;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const id = this.dataset.lineKey ?? '';
    const quantity = Number(this.dataset.quantity) || 1;
    try {
      await mutateCart(
        this,
        'change',
        { id, quantity, selling_plan: Number(this.dataset.sellingPlan) },
        { action: 'update', lines: [{ id, quantity }] }
      );
    } catch (error) {
      console.error('[cart-upgrade]', error);
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * Inline "Subscribe and save" checkbox under a cart drawer line (snippets/cart-line-subscribe.liquid).
 * Ticking switches the line to the offered selling plan; unticking removes the plan (one-time purchase).
 *
 * @typedef {object} LineSubscribeRefs
 * @property {HTMLInputElement} checkbox
 *
 * @extends Component<LineSubscribeRefs>
 */
class CartLineSubscribeComponent extends Component {
  requiredRefs = ['checkbox'];

  async toggle() {
    const { checkbox } = this.refs;
    const subscribe = checkbox.checked;
    if (this.getAttribute('aria-busy') === 'true') {
      checkbox.checked = !subscribe;
      return;
    }
    this.setAttribute('aria-busy', 'true');
    const id = this.dataset.lineKey ?? '';
    const quantity = Number(this.dataset.quantity) || 1;
    const lineIndex = this.dataset.lineIndex;
    try {
      await mutateCart(
        this,
        'change',
        { id, quantity, selling_plan: subscribe ? Number(this.dataset.sellingPlan) : null },
        { action: 'update', lines: [{ id, quantity }] }
      );
      // The line gets a new key, so the drawer may swap the row; keep keyboard focus on its checkbox.
      requestAnimationFrame(() => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const next = document.querySelector(
          `#cart-drawer cart-line-subscribe-component[data-line-index="${lineIndex}"] input`
        );
        if (next instanceof HTMLInputElement) next.focus({ preventScroll: true });
      });
    } catch (error) {
      console.error('[cart-line-subscribe]', error);
      checkbox.checked = !subscribe;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/**
 * One-click upsell inside the cart.
 *
 * @typedef {object} UpsellRefs
 * @property {HTMLButtonElement} button
 *
 * @extends Component<UpsellRefs>
 */
class CartUpsellComponent extends Component {
  async add() {
    const { button } = this.refs;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const id = Number(this.dataset.variantId);
    try {
      await mutateCart(
        this,
        'add',
        { items: [{ id, quantity: 1 }] },
        { action: 'add', lines: [{ merchandiseId: String(id), quantity: 1 }] }
      );
    } catch (error) {
      console.error('[cart-upsell]', error);
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * Keeps the free gift in the cart only while the cart qualifies (subscription quantity >= minimum).
 * The gift's price is made $0 by an automatic discount configured in Shopify admin.
 *
 * @extends Component
 */
class CartGiftComponent extends Component {
  /** Shared across instances so the drawer and cart page never add the gift twice. */
  static #syncing = false;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal: this.#controller.signal });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartLinesUpdateEvent)) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source !== 'cart-gift') this.#sync();
      })
      .catch(() => {});
  };

  async #sync() {
    if (CartGiftComponent.#syncing) return;
    CartGiftComponent.#syncing = true;
    try {
      const giftVariantId = Number(this.dataset.giftVariantId);
      const minimum = Number(this.dataset.minQuantity) || 1;
      if (!giftVariantId) return;

      /** @type {{ items: Array<{ key: string, variant_id: number, quantity: number, properties: Record<string, string> | null, selling_plan_allocation: object | null }> }} */
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      const subscriptionUnits = cart.items
        .filter((item) => item.selling_plan_allocation && !item.properties?.[GIFT_PROPERTY] && !item.properties?.[BUNDLE_GIFT_PROPERTY])
        .reduce((sum, item) => sum + item.quantity, 0);
      const giftLine = cart.items.find((item) => item.properties?.[GIFT_PROPERTY]);
      const qualifies = subscriptionUnits >= minimum;

      if (qualifies && !giftLine) {
        await mutateCart(
          this,
          'add',
          { items: [{ id: giftVariantId, quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }] },
          { action: 'add', lines: [{ merchandiseId: String(giftVariantId), quantity: 1 }] },
          'cart-gift'
        );
      } else if (giftLine && (!qualifies || giftLine.quantity !== 1)) {
        const quantity = qualifies ? 1 : 0;
        await mutateCart(
          this,
          'change',
          { id: giftLine.key, quantity },
          { action: quantity ? 'update' : 'remove', lines: [{ id: giftLine.key, quantity }] },
          'cart-gift'
        );
      }
    } catch (error) {
      console.error('[cart-gift]', error);
    } finally {
      CartGiftComponent.#syncing = false;
    }
  }

}

/**
 * Keeps bundle gifts honest: a gift line (BUNDLE_GIFT_PROPERTY = units it needs) is removed when the cart's
 * other units drop below that number, and capped at one.
 *
 * @extends Component
 */
class CartBundleGiftGuardComponent extends Component {
  static #syncing = false;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal: this.#controller.signal });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartLinesUpdateEvent)) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source !== 'bundle-gift-guard') this.#sync();
      })
      .catch(() => {});
  };

  async #sync() {
    if (CartBundleGiftGuardComponent.#syncing) return;
    CartBundleGiftGuardComponent.#syncing = true;
    try {
      /** @type {{ items: Array<{ key: string, quantity: number, properties: Record<string, string> | null }> }} */
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      const isGift = (/** @type {{ properties: Record<string, string> | null }} */ item) =>
        Boolean(item.properties?.[BUNDLE_GIFT_PROPERTY] || item.properties?.[GIFT_PROPERTY]);
      const units = cart.items.filter((item) => !isGift(item)).reduce((sum, item) => sum + item.quantity, 0);

      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of cart.items) {
        const needed = Number(item.properties?.[BUNDLE_GIFT_PROPERTY]);
        if (!needed) continue;
        if (units < needed) updates[item.key] = 0;
        else if (item.quantity > 1) updates[item.key] = 1;
      }
      const keys = Object.keys(updates);
      if (!keys.length) return;
      await mutateCart(
        this,
        'update',
        { updates },
        {
          action: keys.some((key) => updates[key] === 0) ? 'remove' : 'update',
          lines: keys.map((key) => ({ id: key, quantity: updates[key] ?? 0 })),
        },
        'bundle-gift-guard'
      );
    } catch (error) {
      console.error('[bundle-gift-guard]', error);
    } finally {
      CartBundleGiftGuardComponent.#syncing = false;
    }
  }
}

/**
 * Free-shipping progress for carts in a non-store currency. The threshold is configured in the store currency;
 * this converts it with Shopify's live presentment rate (`Shopify.currency.rate`, the rate checkout uses) and
 * rewrites the message and progress. Store-currency carts are fully rendered by Liquid and skip this.
 *
 * @typedef {object} FreeShippingRefs
 * @property {HTMLElement} text
 * @property {HTMLElement} track
 * @property {HTMLElement} fill
 *
 * @extends Component<FreeShippingRefs>
 */
class FreeShippingBarComponent extends Component {
  requiredRefs = ['text', 'track', 'fill'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  #render() {
    if (!this.hasAttribute('data-convert')) return;
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const threshold = Math.round(Number(this.dataset.threshold) * rate);
    const total = Number(this.dataset.total) || 0;
    const remaining = threshold - total;
    const progress = threshold > 0 ? Math.min(100, Math.max(0, Math.floor((total * 100) / threshold))) : 100;
    const { text, track, fill } = this.refs;

    if (remaining <= 0) {
      text.textContent = this.dataset.unlockedText ?? '';
      this.classList.add('cart-extras-shipping--unlocked');
    } else {
      const amount = formatMoney(remaining, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
      text.innerHTML = (this.dataset.remainingTemplate ?? '').replace('__AMOUNT__', amount);
      this.classList.remove('cart-extras-shipping--unlocked');
    }
    fill.style.width = `${remaining <= 0 ? 100 : progress}%`;
    track.setAttribute('aria-valuenow', String(remaining <= 0 ? 100 : progress));
    this.setAttribute('data-ready', '');
  }
}

if (!customElements.get('cart-upgrade-component')) customElements.define('cart-upgrade-component', CartUpgradeComponent);
if (!customElements.get('cart-line-subscribe-component')) {
  customElements.define('cart-line-subscribe-component', CartLineSubscribeComponent);
}
if (!customElements.get('cart-upsell-component')) customElements.define('cart-upsell-component', CartUpsellComponent);
if (!customElements.get('cart-gift-component')) customElements.define('cart-gift-component', CartGiftComponent);
if (!customElements.get('cart-bundle-gift-guard-component')) {
  customElements.define('cart-bundle-gift-guard-component', CartBundleGiftGuardComponent);
}
if (!customElements.get('free-shipping-bar-component')) customElements.define('free-shipping-bar-component', FreeShippingBarComponent);
